import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { PaperPlaneTilt } from "@phosphor-icons/react";
import { throwIfNotOk } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { Button } from "@/components/ui/button";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Textarea } from "@/components/ui/textarea";
import type { CompanyBillingModel } from "@/types/company";
import type { CompanyPaymentRequest } from "@/types/company-billing";
import { SUPERVISOR_PAYMENT_REQUESTS_KEY } from "./use-supervisor-billing";

/**
 * Kredit: zahtjev za predračun dopune (iznos + napomena).
 * Faktura: zahtjev za fakturu za dosadašnji dio mjeseca (napomena).
 */
export function PaymentRequestForm({
  billingModel,
  hasPending,
}: {
  billingModel: CompanyBillingModel;
  /** Već postoji zahtjev ovog tipa na čekanju (server dopušta samo jedan). */
  hasPending: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isCredit = billingModel === "credit";
  const [amount, setAmount] = useState<number | null>(null);
  const [note, setNote] = useState("");

  const mutation = useMutation({
    mutationFn: async () => {
      const body = isCredit
        ? { type: "credit_topup", amount, note: note.trim() || undefined }
        : { type: "interim_invoice", note: note.trim() || undefined };
      const res = await fetch("/api/supervisor/payment-requests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      await throwIfNotOk(res, t("supervisor.billing.requests.error"));
      return res.json() as Promise<CompanyPaymentRequest>;
    },
    onSuccess: () => {
      toast.success(t("supervisor.billing.requests.success"));
      setAmount(null);
      setNote("");
      queryClient.invalidateQueries({ queryKey: SUPERVISOR_PAYMENT_REQUESTS_KEY });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const valid = !isCredit || (amount !== null && amount > 0);
  const disabled = hasPending || mutation.isPending;

  return (
    <form
      className="space-y-3 px-5 py-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid && !disabled) mutation.mutate();
      }}
    >
      <div>
        <h3 className="text-sm font-semibold text-foreground">
          {isCredit ? t("supervisor.billing.requests.creditTitle") : t("supervisor.billing.requests.invoiceTitle")}
        </h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {isCredit ? t("supervisor.billing.requests.creditHint") : t("supervisor.billing.requests.invoiceHint")}
        </p>
      </div>

      {isCredit && (
        <div className="max-w-xs">
          <label htmlFor="billing-request-amount" className="text-xs font-semibold text-muted-foreground">
            {t("supervisor.billing.requests.amount")}
          </label>
          <CurrencyInput
            id="billing-request-amount"
            value={amount}
            onChange={setAmount}
            placeholder={t("supervisor.billing.requests.amountPlaceholder")}
            disabled={disabled}
          />
        </div>
      )}

      <div>
        <label htmlFor="billing-request-note" className="text-xs font-semibold text-muted-foreground">
          {t("supervisor.billing.requests.note")}
        </label>
        <Textarea
          id="billing-request-note"
          value={note}
          maxLength={1000}
          onChange={(e) => setNote(e.target.value)}
          disabled={disabled}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" size="sm" disabled={!valid || disabled}>
          <PaperPlaneTilt className="size-4" />
          {t("supervisor.billing.requests.submit")}
        </Button>
        {hasPending && (
          <span className="text-xs text-muted-foreground">{t("supervisor.billing.requests.pendingExists")}</span>
        )}
      </div>
    </form>
  );
}
